import {Hotel} from 'modules/sapiSearch/slice'

import {
  ANCHOR_REVIEW_COUNT_RATIO,
  DIFFERENT_PROPERTY_TYPE_PENALTY,
  DISTANCE_PENALTY_PER_KM,
  EXTRA_DISTANCE_PENALTY_PER_KM,
  FAIR_PREMIUM_BASE,
  LOW_REVIEW_CONFIDENCE_PENALTY,
  MAXIMUM_PRICE_RATIO,
  MAXIMUM_REVIEW_COUNT,
  MAXIMUM_VALUE_CONTRIBUTION,
  MINIMUM_PRICE_RATIO,
  MINIMUM_REVIEW_COUNT,
  MISSING_QUALITY_FIELD_PENALTY,
  PREFERRED_RADIUS_KM,
  PRICE_TIER_PENALTY_WEIGHT,
  QUALITY_PER_RATING_POINT,
  QUALITY_PER_STAR,
  QUALITY_SCORE_WEIGHT,
  RATING_DROP_ALLOWANCE,
  RATING_DROP_PENALTY,
  SIMILAR_HOTELS_LIMIT,
  STAR_DIFFERENCE_ALLOWANCE,
  STAR_DIFFERENCE_PENALTY,
  UNKNOWN_DISTANCE_PENALTY,
  UNKNOWN_PROPERTY_TYPE_PENALTY
} from '../constants'
import {getDistanceKm} from './getDistanceKm'

import type {RecommendationInput, SimilarHotel} from '../types'

// Penalize hotels farther from the anchor, more strongly outside the preferred area.
const getDistancePenalty = (distance?: number) => {
  if (distance === undefined) return UNKNOWN_DISTANCE_PENALTY

  return (
    DISTANCE_PENALTY_PER_KM * distance +
    EXTRA_DISTANCE_PENALTY_PER_KM * Math.max(0, distance - PREFERRED_RADIUS_KM)
  )
}

const getPropertyTypePenalty = (anchor: Hotel, hotel: Hotel) => {
  if (
    !Number.isFinite(anchor.propertyTypeId) ||
    !Number.isFinite(hotel.propertyTypeId)
  )
    return UNKNOWN_PROPERTY_TYPE_PENALTY

  if (hotel.propertyTypeId === anchor.propertyTypeId) return 0

  return DIFFERENT_PROPERTY_TYPE_PENALTY
}

// Bound the evidence requirement so sparse and heavily reviewed anchors remain comparable.
const getReviewConfidence = (
  anchorReviews: number,
  candidateReviews: number
) => {
  const minimumReviews = Math.min(
    MAXIMUM_REVIEW_COUNT,
    Math.max(
      MINIMUM_REVIEW_COUNT,
      (anchorReviews || 0) * ANCHOR_REVIEW_COUNT_RATIO
    )
  )
  const reviews = Number.isFinite(candidateReviews)
    ? Math.max(0, candidateReviews)
    : 0

  return Math.min(1, reviews / minimumReviews)
}

const getPricePenalty = (premium?: number) => {
  if (premium === undefined) return 0

  return (
    PRICE_TIER_PENALTY_WEIGHT *
    Math.max(
      0,
      Math.log(MINIMUM_PRICE_RATIO) - premium,
      premium - Math.log(MAXIMUM_PRICE_RATIO)
    )
  )
}

/**
 * Find alternatives to the anchor by balancing value, quality and proximity.
 * Use the supplied card prices so comparisons match what the user sees.
 * Similarity penalties lower scores without excluding hotels.
 * Skip the anchor and hotels without a valid price, then return the top recommendations.
 */
export const rankSimilarHotels = ({
  anchorId,
  displayPrices,
  hotels
}: RecommendationInput) => {
  const anchor = hotels[anchorId]
  const recommendations: SimilarHotel[] = []

  if (!anchor)
    return {
      recommendations
    }

  const anchorDisplayPrice = displayPrices[anchorId]
  const anchorPrice =
    Number.isFinite(anchorDisplayPrice) && anchorDisplayPrice > 0
      ? anchorDisplayPrice
      : undefined

  for (const hotel of Object.values(hotels)) {
    if (hotel.objectID === anchorId) continue

    const price = displayPrices[hotel.objectID]

    if (!Number.isFinite(price) || price <= 0) continue

    const distance = getDistanceKm(anchor._geoloc, hotel._geoloc)
    const distancePenalty = getDistancePenalty(distance)
    const propertyTypePenalty = getPropertyTypePenalty(anchor, hotel)

    const starsKnown = [anchor.starRating, hotel.starRating].every(
      stars => Number.isFinite(stars) && stars >= 1 && stars <= 5
    )
    const ratingsKnown = [
      anchor.guestRating?.overall,
      hotel.guestRating?.overall
    ].every(rating => Number.isFinite(rating) && rating > 0 && rating <= 10)
    const starDifference = starsKnown ? hotel.starRating - anchor.starRating : 0
    const ratingDifference = ratingsKnown
      ? hotel.guestRating.overall - anchor.guestRating.overall
      : undefined
    const confidence = getReviewConfidence(
      anchor.reviewCount,
      hotel.reviewCount
    )
    const reviewPenalty = LOW_REVIEW_CONFIDENCE_PENALTY * (1 - confidence)
    const missingQualityPenalty =
      (starsKnown ? 0 : MISSING_QUALITY_FIELD_PENALTY) +
      (ratingsKnown ? 0 : MISSING_QUALITY_FIELD_PENALTY)
    const starPenalty =
      STAR_DIFFERENCE_PENALTY *
      Math.max(0, Math.abs(starDifference) - STAR_DIFFERENCE_ALLOWANCE)
    const ratingPenalty =
      RATING_DROP_PENALTY *
      Math.max(0, -(ratingDifference ?? 0) - RATING_DROP_ALLOWANCE)

    // Estimate how much more or less the hotel is worth based on its stars and guest rating.
    const quality =
      QUALITY_PER_RATING_POINT * (ratingDifference ?? 0) * confidence +
      QUALITY_PER_STAR * starDifference
    const premium = anchorPrice ? Math.log(price / anchorPrice) : undefined
    const priceDifference = anchorPrice ? price / anchorPrice - 1 : undefined
    const pricePenalty = getPricePenalty(premium)
    const totalPenalty =
      distancePenalty +
      propertyTypePenalty +
      reviewPenalty +
      missingQualityPenalty +
      starPenalty +
      ratingPenalty +
      pricePenalty

    // Reward savings and quality improvements relative to the anchor.
    // A more expensive hotel needs better quality to justify its price.
    const value =
      premium === undefined ? undefined : FAIR_PREMIUM_BASE + quality - premium
    // Rank by value and quality after accounting for similarity penalties.
    // Limit the value bonus so a large discount does not dominate the comparison.
    const score =
      Math.min(MAXIMUM_VALUE_CONTRIBUTION, value ?? 0) +
      QUALITY_SCORE_WEIGHT * quality -
      totalPenalty

    recommendations.push({
      hotel,
      displayPrice: price,
      distanceKm: distance,
      priceDifference,
      ratingDifference,
      value,
      score
    })
  }

  // Show the highest scores first, preferring closer hotels when scores tie.
  // Use hotel IDs to keep the order stable when both score and distance match.
  recommendations.sort(
    (a, b) =>
      b.score - a.score ||
      (a.distanceKm ?? Infinity) - (b.distanceKm ?? Infinity) ||
      a.hotel.objectID.localeCompare(b.hotel.objectID)
  )

  return {
    recommendations: recommendations.slice(0, SIMILAR_HOTELS_LIMIT)
  }
}
